import React, { useCallback, useMemo } from 'react';
import { FlatList, InteractionManager, View } from 'react-native';
import { rem, TextButton } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { isDefaultCategory } from 'hooks/useCategory';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectNormalizedCategoriesWithTransactionsCount, selectParentCategoriesForKeyboardCategories, } from 'reducers/selectors';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import EmptyComponent from './KeyboardEmpty';
import KeyboardCategoriesRow from './KeyboardCategoriesRow';
import { isDesktopOrWeb } from '../../../../constants';
const keyExtractor = (item) => item.id;
const strings = {
    empty: 'There are no categories available in any of your transactions',
};
const KeyboardCategories = ({ onSelect, onSelectAll, activeFilters, settingsFilter, onRemoveFilter, contentContainerStyle, }) => {
    const categories = useAppSelector(selectNormalizedCategoriesWithTransactionsCount);
    const parentCategories = useAppSelector(selectParentCategoriesForKeyboardCategories);
    const { isUnlockedForSpace: isCustomCategoriesUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'customCategories',
    });
    const activeFilterMap = useMemo(() => activeFilters?.reduce((acc, curr) => {
        acc[curr.id] = !curr.parentCategoryId || curr.isUserSelected;
        return acc;
    }, {}), [activeFilters]);
    const type = useMemo(() => (settingsFilter === 'AND' ? 'single' : 'multiple'), [settingsFilter]);
    const renderItem = useCallback(({ item }) => (<KeyboardCategoriesRow item={item} type={type} activeFilterMap={activeFilterMap} onPress={(category) => {
            if (!isCustomCategoriesUnlockedForSpace && !isDefaultCategory(item.id)) {
                onPressUpgrade();
                return;
            }
            onSelect('KeyboardCategories', {
                ...category,
                badgeText: category.displayName,
                filterType: 'CATEGORY',
                isUserSelected: true,
            });
        }} isOtherCategorySelected={!!activeFilters?.find((filter) => filter.displayName === 'Others' && filter.id === item.id)}/>), [activeFilterMap, activeFilters, isCustomCategoriesUnlockedForSpace, onPressUpgrade, onSelect, type]);
    const styles = useStyles(styleSet);
    const flatListStyles = useMarginBottom('paddingBottom', undefined, contentContainerStyle);
    const Content = useMemo(() => {
        const baseProps = {
            renderItem,
            keyExtractor,
            data: parentCategories,
            style: styles.flatList,
            contentContainerStyle: flatListStyles,
            ListEmptyComponent: <EmptyComponent description={strings.empty}/>,
        };
        return <FlatList {...baseProps}/>;
    }, [parentCategories, flatListStyles, renderItem, styles.flatList]);
    const isAllSelected = useMemo(() => Object.keys(categories).length === activeFilters?.length, [activeFilters?.length, categories]);
    const onSelectUnselectAll = useCallback(() => {
        InteractionManager.runAfterInteractions(() => {
            if (isAllSelected) {
                onRemoveFilter('CATEGORY');
            }
            else {
                onSelectAll(parentCategories
                    .map((item) => [
                    {
                        ...item,
                        badgeText: item.displayName,
                        filterType: 'CATEGORY',
                        isUserSelected: false,
                    },
                    ...(item.subCategoryIds ?? [])
                        .map((id) => ({
                        ...categories[id],
                        badgeText: categories[id]?.displayName,
                        filterType: 'CATEGORY',
                        isUserSelected: false,
                    }))
                        .filter((a) => !!a.badgeText),
                ])
                    .flat());
            }
        });
    }, [isAllSelected, onRemoveFilter, onSelectAll, parentCategories, categories]);
    const RightContent = useMemo(() => {
        if (settingsFilter !== 'AND' && parentCategories.length) {
            return <TextButton text={isAllSelected ? 'Deselect all' : 'Select all'} onPress={onSelectUnselectAll}/>;
        }
        return null;
    }, [parentCategories.length, isAllSelected, onSelectUnselectAll, settingsFilter]);
    return (<>
      <View style={contentContainerStyle}>
        <SectionHeader horizontalMargins style={styles.sectionHeader} button={RightContent}>
          Categories
        </SectionHeader>
      </View>
      {Content}
    </>);
};
const styleSet = createStyleSheets(() => ({
    flatList: {
        ...(isDesktopOrWeb && {
            padding: 8,
        }),
    },
    sectionHeader: {
        paddingTop: rem(12),
    },
}));
export default KeyboardCategories;
